/**
 * Un espacio del menú: se despliega sin navegar. Plegado a íconos abre una
 * lista lateral con sus entradas. Si está plegado y alguna entrada trae un
 * contador, el espacio muestra la suma para que lo pendiente no se esconda.
 */
import { ChevronRight } from 'lucide-react';
import { useId } from 'react';
import { NavLink } from 'react-router-dom';

import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { SidebarMenuButton, SidebarMenuItem, SidebarMenuSub } from '@/components/ui/sidebar';
import { NAV_META } from '@/copy/nav';
import { cn } from '@/lib/utils';

import { ContadorDelMenu, EntradaDelMenu } from './EntradaDelMenu';
import { type Contadores, contadorDe } from './contadores';
import type { Espacio } from './registro';

interface Props {
  espacio: Espacio;
  abierto: boolean;
  /** Menú lateral plegado a íconos (en el teléfono nunca lo está). */
  plegadoAIconos: boolean;
  /** Id de la entrada activa de toda la pantalla, o null. */
  entradaActivaId: string | null;
  contadores: Contadores;
  onAlternar: () => void;
  onElegir: () => void;
}

export function EspacioDelMenu({
  espacio,
  abierto,
  plegadoAIconos,
  entradaActivaId,
  contadores,
  onAlternar,
  onElegir,
}: Props) {
  const idLista = useId();
  const nombre = NAV_META[espacio.id]?.label ?? espacio.id;
  const Icono = espacio.icon;
  const contieneActiva = espacio.entradas.some((e) => e.id === entradaActivaId);
  const pendientes = espacio.entradas.reduce((suma, e) => suma + contadorDe(e, contadores), 0);

  if (plegadoAIconos) {
    return (
      <SidebarMenuItem>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <SidebarMenuButton
              isActive={contieneActiva}
              aria-label={nombre}
              tooltip={{ children: nombre }}
              className="font-medium"
            >
              <Icono className="text-fg-muted shrink-0" />
              <span>{nombre}</span>
              <ContadorDelMenu cantidad={pendientes} />
            </SidebarMenuButton>
          </DropdownMenuTrigger>
          <DropdownMenuContent side="right" align="start" className="min-w-48">
            <DropdownMenuLabel>{nombre}</DropdownMenuLabel>
            {espacio.entradas.map((entrada) => (
              <DropdownMenuItem key={entrada.id} asChild>
                <NavLink
                  to={entrada.to}
                  onClick={onElegir}
                  aria-current={entrada.id === entradaActivaId ? 'page' : undefined}
                >
                  <span className="flex-1 truncate">{NAV_META[entrada.id]?.label ?? entrada.id}</span>
                  <ContadorDelMenu cantidad={contadorDe(entrada, contadores)} />
                </NavLink>
              </DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
      </SidebarMenuItem>
    );
  }

  return (
    <SidebarMenuItem>
      <SidebarMenuButton
        type="button"
        isActive={contieneActiva && !abierto}
        aria-expanded={abierto}
        aria-controls={abierto ? idLista : undefined}
        onClick={onAlternar}
        className="font-medium"
      >
        <Icono className="text-fg-muted shrink-0" />
        <span>{nombre}</span>
        {!abierto && <ContadorDelMenu cantidad={pendientes} />}
        <ChevronRight
          className={cn('text-fg-muted ml-auto shrink-0 transition-transform', abierto && 'rotate-90')}
          aria-hidden
        />
      </SidebarMenuButton>
      {abierto && (
        <SidebarMenuSub id={idLista}>
          {espacio.entradas.map((entrada) => (
            <EntradaDelMenu
              key={entrada.id}
              entrada={entrada}
              anidada
              activa={entrada.id === entradaActivaId}
              contadores={contadores}
              onElegir={onElegir}
            />
          ))}
        </SidebarMenuSub>
      )}
    </SidebarMenuItem>
  );
}
